/**
 * Application error hierarchy for the FIM platform.
 *
 * Every error carries an HTTP `statusCode`, a stable machine-readable `code`,
 * and an `isOperational` flag distinguishing expected/operational failures
 * (bad input, missing resource, auth) from unexpected programmer errors. HTTP
 * layers should serialize operational errors to clients and treat
 * non-operational ones as opaque 500s.
 */

/** Stable, machine-readable error codes emitted across the platform. */
export const ErrorCode = {
  INTERNAL: 'INTERNAL_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  VALIDATION: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  RATE_LIMIT: 'RATE_LIMITED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** Structured field-level validation issue. */
export interface ValidationIssue {
  /** Dotted/array path to the offending field, e.g. `user.email`. */
  readonly path: string;
  /** Human-readable message describing the problem. */
  readonly message: string;
  /** Optional validator-specific code, e.g. `too_small`. */
  readonly code?: string;
}

/** Options accepted by every {@link AppError}. */
export interface AppErrorOptions {
  /** HTTP status code associated with this error. */
  readonly statusCode?: number;
  /** Stable machine-readable code. */
  readonly code?: string;
  /**
   * Whether the error is operational (expected) versus a programmer error.
   * Operational errors are safe to surface to API consumers.
   */
  readonly isOperational?: boolean;
  /** The underlying cause, preserved for logging and stack chaining. */
  readonly cause?: unknown;
  /** Arbitrary structured context attached for debugging/telemetry. */
  readonly details?: unknown;
}

/** Shape produced by {@link AppError.toJSON}, safe for API responses. */
export interface SerializedError {
  readonly name: string;
  readonly message: string;
  readonly code: string;
  readonly statusCode: number;
  readonly details?: unknown;
}

/**
 * Base class for all known application errors.
 *
 * Extends the native `Error` while preserving the prototype chain under
 * transpilation, capturing a clean stack trace, and forwarding `cause` to the
 * standard `Error` options.
 */
export class AppError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly isOperational: boolean;
  public readonly details?: unknown;

  constructor(message: string, options: AppErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });

    this.name = new.target.name;
    this.statusCode = options.statusCode ?? 500;
    this.code = options.code ?? ErrorCode.INTERNAL;
    this.isOperational = options.isOperational ?? true;
    this.details = options.details;

    // Restore prototype chain for instanceof checks after transpilation to ES5/ESNext targets.
    Object.setPrototypeOf(this, new.target.prototype);

    // Exclude the constructor frame from the captured stack when available (V8).
    const captureStackTrace = (
      Error as unknown as {
        captureStackTrace?: (target: object, ctor?: Function) => void;
      }
    ).captureStackTrace;
    if (typeof captureStackTrace === 'function') {
      captureStackTrace(this, new.target);
    }
  }

  /** Serialize to a client-safe plain object. */
  toJSON(): SerializedError {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      ...(this.details === undefined ? {} : { details: this.details }),
    };
  }
}

/** Resource could not be located (HTTP 404). */
export class NotFoundError extends AppError {
  constructor(message = 'Resource not found', options: Omit<AppErrorOptions, 'statusCode' | 'code'> = {}) {
    super(message, { ...options, statusCode: 404, code: ErrorCode.NOT_FOUND, isOperational: true });
  }
}

/** Authentication is required or the supplied credentials are invalid (HTTP 401). */
export class UnauthorizedError extends AppError {
  constructor(message = 'Authentication required', options: Omit<AppErrorOptions, 'statusCode' | 'code'> = {}) {
    super(message, { ...options, statusCode: 401, code: ErrorCode.UNAUTHORIZED, isOperational: true });
  }
}

/** The caller is authenticated but lacks permission (HTTP 403). */
export class ForbiddenError extends AppError {
  constructor(message = 'You do not have permission to perform this action', options: Omit<AppErrorOptions, 'statusCode' | 'code'> = {}) {
    super(message, { ...options, statusCode: 403, code: ErrorCode.FORBIDDEN, isOperational: true });
  }
}

/**
 * Input failed validation (HTTP 422).
 *
 * Carries a structured list of field-level {@link ValidationIssue}s in addition
 * to the generic `details` payload.
 */
export class ValidationError extends AppError {
  public readonly issues: readonly ValidationIssue[];

  constructor(
    message = 'The request payload is invalid',
    issues: readonly ValidationIssue[] = [],
    options: Omit<AppErrorOptions, 'statusCode' | 'code' | 'details'> = {},
  ) {
    super(message, {
      ...options,
      statusCode: 422,
      code: ErrorCode.VALIDATION,
      isOperational: true,
      details: { issues },
    });
    this.issues = issues;
  }
}

/** The request conflicts with the current state of the resource (HTTP 409). */
export class ConflictError extends AppError {
  constructor(message = 'The request conflicts with the current resource state', options: Omit<AppErrorOptions, 'statusCode' | 'code'> = {}) {
    super(message, { ...options, statusCode: 409, code: ErrorCode.CONFLICT, isOperational: true });
  }
}

/** Options specific to {@link RateLimitError}. */
export interface RateLimitErrorOptions extends Omit<AppErrorOptions, 'statusCode' | 'code'> {
  /** Seconds the client should wait before retrying (maps to `Retry-After`). */
  readonly retryAfterSeconds?: number;
}

/** Too many requests in a given window (HTTP 429). */
export class RateLimitError extends AppError {
  public readonly retryAfterSeconds?: number;

  constructor(message = 'Too many requests, please slow down', options: RateLimitErrorOptions = {}) {
    const { retryAfterSeconds, ...rest } = options;
    super(message, { ...rest, statusCode: 429, code: ErrorCode.RATE_LIMIT, isOperational: true });
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * An unexpected, non-operational failure (HTTP 500).
 *
 * Defaults `isOperational` to `false` so monitoring can distinguish genuine
 * bugs from anticipated failures.
 */
export class InternalError extends AppError {
  constructor(message = 'An unexpected error occurred', options: Omit<AppErrorOptions, 'statusCode' | 'code'> = {}) {
    super(message, { isOperational: false, ...options, statusCode: 500, code: ErrorCode.INTERNAL });
  }
}

/** Type guard narrowing an unknown value to {@link AppError}. */
export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * Normalize any thrown value into an {@link AppError}.
 *
 * Already-{@link AppError} values pass through unchanged; native `Error`s and
 * arbitrary thrown values are wrapped in a non-operational {@link InternalError}
 * with the original preserved as `cause`.
 */
export function toAppError(value: unknown): AppError {
  if (isAppError(value)) return value;
  if (value instanceof Error) {
    return new InternalError(value.message, { cause: value });
  }
  return new InternalError('Unknown error', { cause: value });
}
